/*
 * YipYip — release the buy controls once a choice exists
 * ---------------------------------------------------------------------------
 * The product page opens with NO variant preselected (the rule, and why
 * `product.selected_variant` is the right test, are at the top of
 * snippets/product-variant-picker.liquid). Most of what follows from that is
 * server-rendered and needs no script: the picker comes back checked from the
 * `?option_values=` refetch, the price and the stock pill are re-rendered by
 * `updateSourceFromDestination`, and product-form.js's
 * `toggleSubmitButton(false)` restores "Add to cart" on its own.
 *
 * Two controls are left out of all of that, both for the same reason —
 * `handleUpdateProductInfo` (product-info.js:174) updates the price, the media,
 * the option values and the main submit button, and re-renders nothing else:
 *
 *   1. The one-tap checkout (`payment_button`). It buys the form's hidden `id`
 *      without passing through the cart, so on a cold page it would sell a
 *      colour nobody picked. It cannot merely be omitted from the cold render,
 *      because nothing would ever put it back short of a page reload. It is
 *      rendered, hidden by `.yy-awaiting-choice` on the buy row, and revealed
 *      here.
 *   2. The sticky add-to-cart button. It sits outside <product-form> and reaches
 *      the buy form through `form="..."`, so product-form.js never sees it. It
 *      carries `data-yy-awaiting-choice` and its real caption in `data-label`.
 *
 * Listening for `change` on the controls themselves, rather than subscribing to
 * Dawn's `variantChange`, is deliberate: these must release the moment the
 * shopper commits to an option, not when the network round-trip that resolves
 * it returns. Those are several hundred milliseconds apart on a slow
 * connection, and a buy button that appears late reads as a broken page.
 *
 * Without JS both controls simply stay in their cold state, and the ordinary
 * Add to cart — server-rendered, and enabled by the refetch — still works.
 */
(function () {
  'use strict';

  var AWAITING_ROW = 'yy-awaiting-choice';
  var AWAITING_BTN = '[data-yy-awaiting-choice]';

  // Both pickers can start a choice: the buy box's <variant-selects>, and the
  // sticky bar's own <secondary-variant-select>, which is rendered outside
  // <product-info> and so cannot be found by walking up from it.
  var PICKERS = 'variant-selects, secondary-variant-select';

  function release() {
    Array.prototype.forEach.call(document.querySelectorAll('.' + AWAITING_ROW), function (row) {
      row.classList.remove(AWAITING_ROW);
    });

    Array.prototype.forEach.call(document.querySelectorAll(AWAITING_BTN), function (button) {
      button.removeAttribute('data-yy-awaiting-choice');
      button.removeAttribute('disabled');

      // The cold caption is "Choose options"; `data-label` holds the merchant's
      // own button text, which is the only place it survives the swap.
      var label = button.getAttribute('data-label');
      var span = button.querySelector('span');
      if (label && span) span.textContent = label;
    });
  }

  // One delegated listener rather than one per input: <variant-selects> swaps
  // its whole subtree for freshly rendered markup on every change
  // (product-info.js:157), so anything bound to an individual radio is thrown
  // away by the first change that would have used it.
  document.addEventListener(
    'change',
    function (event) {
      var target = event.target;
      if (!target || !target.closest || !target.closest(PICKERS)) return;
      release();
    },
    true
  );

  /*
   * The sticky bar's dropdown, brought into step with the buy box.
   *
   * SecondaryVariantSelect syncs one way only -- picking in the sticky bar
   * ticks the matching swatch (global.js:1185), but picking a swatch has never
   * moved the dropdown back. That was already true before the page stopped
   * preselecting: the dropdown was server-rendered on the first variant and
   * simply stayed there, so choosing "Pink 65cm" upstairs left it reading
   * "Grey 40cm". The placeholder makes the staleness visible rather than
   * introducing it -- "Choose options" beside a made choice is obviously
   * wrong, where a plausible-but-wrong variant title was not.
   *
   * This runs on `variantChange` rather than on the raw `change` above because
   * only the resolved variant knows every option: on the LED collar a shopper
   * picks one of three, and Shopify fills in the other two server-side.
   */
  var SELECT = 'secondary-variant-select .variant-dropdown';

  function syncStickySelect(variant) {
    if (!variant || !variant.options) return;
    var select = document.querySelector(SELECT);
    if (!select) return;

    // Option values are `variant.options | join: ','` (main-product.liquid).
    var wanted = variant.options.join(',');
    var match = Array.prototype.filter.call(select.options, function (option) {
      return option.value === wanted;
    })[0];
    if (!match) return;

    // Drop the placeholder once it can no longer be true. Leaving it would let
    // a shopper scroll back to "Choose options" and post an empty value.
    Array.prototype.forEach.call(select.querySelectorAll('option[value=""]'), function (option) {
      option.remove();
    });

    select.value = match.value;
  }

  if (typeof subscribe === 'function') {
    subscribe('variant-change', function (event) {
      syncStickySelect(event && event.data && event.data.variant);
    });
  }
})();
